import React from "react";
import { Link } from "react-router-dom";
import { MarketingLayout } from "./MarketingLayout";
import { MarketingCTA } from "./MarketingCTA";
import { FaqAccordion } from "./FaqAccordion";
import { SEO } from "../seo/SEO";
import { breadcrumbSchema, faqSchema } from "../seo/schema";
import { getMarketingIcon } from "../../data/marketingIcons";
import { FEATURES_BY_SLUG } from "../../data/featuresData";
import { INDUSTRIES_BY_SLUG } from "../../data/industriesData";

/** Reusable /industries/:slug page template. */
export function IndustryTemplate({ data }) {
  const Icon = getMarketingIcon(data.icon);
  const breadcrumb = [{ name: "Home", path: "/" }, { name: "Industries", path: "/industries" }, { name: data.title, path: `/industries/${data.slug}` }];

  return (
    <MarketingLayout breadcrumb={breadcrumb}>
      <SEO
        title={data.metaTitle}
        description={data.metaDescription}
        path={`/industries/${data.slug}`}
        jsonLd={[breadcrumbSchema(breadcrumb), data.faqs?.length ? faqSchema(data.faqs) : null]}
      />

      <section className="relative overflow-hidden">
        <div className="absolute top-[-15%] left-1/2 -translate-x-1/2 h-72 w-[42rem] bg-amber-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 pt-10 pb-14 text-center">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
            <Icon className="h-7 w-7 text-amber-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-5" data-testid="industry-h1">
            {data.title}
          </h1>
          <p className="text-[15.5px] text-slate-400 leading-relaxed max-w-2xl mx-auto">{data.intro}</p>
        </div>
      </section>

      {data.challenges?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Common challenges we hear from {data.shortName || data.title.toLowerCase()} businesses</h2>
          <ul className="space-y-3">
            {data.challenges.map((c, i) => (
              <li key={i} className="flex items-start gap-3 text-[14px] text-slate-300" data-testid={`industry-challenge-${i}`}>
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-white text-center mb-8">How ServiceGuru Pro helps</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {data.benefits.map((b) => (
            <div key={b.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={`industry-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <h3 className="text-white font-semibold text-[15px] mb-1.5">{b.title}</h3>
              <p className="text-[13.5px] text-slate-400 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {data.faqs?.length > 0 && (
        <section className="max-w-3xl mx-auto px-5 lg:px-8 pb-16">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Frequently asked questions</h2>
          <FaqAccordion faqs={data.faqs} />
        </section>
      )}

      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {data.relatedFeatures?.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Features {data.shortName || "you'll"} use most</h3>
              <div className="flex flex-wrap gap-2">
                {data.relatedFeatures.map((slug) => {
                  const rf = FEATURES_BY_SLUG[slug];
                  if (!rf) return null;
                  return (
                    <Link key={slug} to={`/features/${slug}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-amber-400 transition-colors">
                      {rf.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          {data.relatedIndustries?.length > 0 && (
            <div>
              <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Other industries we serve</h3>
              <div className="flex flex-wrap gap-2">
                {data.relatedIndustries.map((slug) => {
                  const ri = INDUSTRIES_BY_SLUG[slug];
                  if (!ri) return null;
                  return (
                    <Link key={slug} to={`/industries/${slug}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-amber-400 transition-colors">
                      {ri.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      <MarketingCTA heading={`Built for ${data.shortName || data.title} businesses`} sub="Start your 14-day free trial — no credit card required." />
    </MarketingLayout>
  );
}
